import * as THREE from 'three';
import { harmonicNoise, vec3 } from './math';
import { createPeriodLowerGarment } from './PeriodGarments';
import type { CharacterPose, CharacterSpec } from './types';

interface CostumeProfile {
  skin: number;
  tunic: number;
  mantle: number;
  trim: number;
  hair: number;
  width: number;
  longRobe: boolean;
}

interface HeroBoneState {
  bone: THREE.Object3D;
  rest: THREE.Quaternion;
}

function material(color: number, roughness = 0.9): THREE.MeshStandardMaterial {
  const base = new THREE.Color(color);
  return new THREE.MeshStandardMaterial({
    color: base,
    roughness,
    metalness: 0,
    flatShading: false,
    emissive: base.clone().multiplyScalar(0.008),
  });
}

let wovenBump: THREE.DataTexture | undefined;

function wovenClothMaterial(color: number): THREE.MeshStandardMaterial {
  if (!wovenBump) {
    const size = 64;
    const pixels = new Uint8Array(size * size);
    for (let y = 0; y < size; y += 1) {
      for (let x = 0; x < size; x += 1) {
        // A deterministic plain weave. The slight irregularity keeps the
        // fabric from reading as a perfectly smooth game-material cylinder.
        const warp = Math.sin(x * Math.PI * 0.5) * 18;
        const weft = Math.sin(y * Math.PI * 0.5) * 14;
        const irregular = Math.sin(x * 1.71 + y * 2.37) * 5;
        pixels[y * size + x] = Math.round(128 + warp + weft + irregular);
      }
    }
    wovenBump = new THREE.DataTexture(pixels, size, size, THREE.RedFormat);
    wovenBump.wrapS = THREE.RepeatWrapping;
    wovenBump.wrapT = THREE.RepeatWrapping;
    wovenBump.repeat.set(7, 11);
    wovenBump.needsUpdate = true;
  }
  const base = new THREE.Color(color);
  return new THREE.MeshStandardMaterial({
    color: base,
    roughness: 0.92,
    metalness: 0,
    bumpMap: wovenBump,
    bumpScale: 0.018,
    emissive: base.clone().multiplyScalar(0.005),
  });
}

function robeGeometry(topRadius: number, bottomRadius: number, height: number): THREE.CylinderGeometry {
  const geometry = new THREE.CylinderGeometry(topRadius, bottomRadius, height, 28, 10, false);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < positions.count; index += 1) {
    const x = positions.getX(index);
    const y = positions.getY(index);
    const z = positions.getZ(index);
    const angle = Math.atan2(z, x);
    const fromHem = THREE.MathUtils.clamp((y + height / 2) / height, 0, 1);
    const pleat = Math.sin(angle * 7 + 0.35) * (0.035 + (1 - fromHem) * 0.085);
    const irregular = Math.sin(angle * 3 - 0.8) * 0.018 * (1 - fromHem);
    const fold = 1 + pleat + irregular;
    positions.setXYZ(index, x * fold, y + Math.sin(angle * 4) * 0.045 * Math.pow(1 - fromHem, 3), z * fold);
  }
  geometry.computeVertexNormals();
  return geometry;
}

function drapeGeometry(width: number, height: number): THREE.PlaneGeometry {
  const geometry = new THREE.PlaneGeometry(width, height, 5, 10);
  const positions = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let index = 0; index < positions.count; index += 1) {
    let x = positions.getX(index);
    const y = positions.getY(index);
    const normalized = y / height + 0.5;
    x *= 0.74 + normalized * 0.26;
    const z = Math.sin((y / height + 0.5) * Math.PI * 4) * 0.025 + Math.pow(x / width, 2) * 0.035;
    positions.setXYZ(index, x, y, z);
  }
  geometry.computeVertexNormals();
  return geometry;
}

function tunicShellGeometry(female: boolean): THREE.BufferGeometry {
  const segments = 36;
  const rings = female
    ? [
        { y: 1.03, rx: 0.165, rz: 0.125 },
        { y: 1.17, rx: 0.176, rz: 0.14 },
        { y: 1.34, rx: 0.205, rz: 0.175 },
        { y: 1.49, rx: 0.245, rz: 0.2 },
        { y: 1.57, rx: 0.27, rz: 0.19 },
        { y: 1.62, rx: 0.285, rz: 0.17 },
        { y: 1.66, rx: 0.25, rz: 0.145 },
        { y: 1.69, rx: 0.17, rz: 0.11 },
        { y: 1.72, rx: 0.092, rz: 0.074 },
      ]
    : [
        { y: 1.07, rx: 0.31, rz: 0.22 },
        { y: 1.25, rx: 0.275, rz: 0.2 },
        { y: 1.57, rx: 0.295, rz: 0.205 },
        { y: 1.75, rx: 0.35, rz: 0.192 },
        { y: 1.87, rx: 0.11, rz: 0.086 },
      ];
  const positions: number[] = [];
  const indices: number[] = [];
  for (let ringIndex = 0; ringIndex < rings.length; ringIndex += 1) {
    const ring = rings[ringIndex]!;
    for (let segment = 0; segment < segments; segment += 1) {
      const angle = (segment / segments) * Math.PI * 2;
      const front = Math.sin(angle);
      const fromWaist = ringIndex / Math.max(1, rings.length - 1);
      const clothEase = 1 + Math.sin(angle * 7 + 0.4) * (0.018 + (1 - fromWaist) * 0.032);
      positions.push(
        Math.cos(angle) * ring.rx * clothEase,
        ring.y + (ringIndex === 0 ? Math.sin(angle * 3) * 0.012 : 0),
        front * ring.rz * (front < 0 ? 1.08 : 0.98),
      );
    }
  }
  for (let ringIndex = 0; ringIndex < rings.length - 1; ringIndex += 1) {
    for (let segment = 0; segment < segments; segment += 1) {
      const next = (segment + 1) % segments;
      const a = ringIndex * segments + segment;
      const b = ringIndex * segments + next;
      const c = (ringIndex + 1) * segments + next;
      const d = (ringIndex + 1) * segments + segment;
      // Ring vertices advance counter-clockwise in X/Z. Winding from the
      // lower ring up to the next ring keeps normals facing away from the
      // body; the previous order pointed them inward and made the cloth read
      // as a flat black box under directional light.
      indices.push(a, d, b, b, d, c);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function marySkirtGeometry(): THREE.BufferGeometry {
  const segments = 40;
  const rings = [
    { y: 1.08, rx: 0.21, rz: 0.14, fold: 0.008 },
    { y: 0.84, rx: 0.255, rz: 0.17, fold: 0.018 },
    { y: 0.47, rx: 0.33, rz: 0.215, fold: 0.038 },
    { y: 0.1, rx: 0.405, rz: 0.26, fold: 0.058 },
  ];
  const positions: number[] = [];
  const indices: number[] = [];
  for (let ringIndex = 0; ringIndex < rings.length; ringIndex += 1) {
    const ring = rings[ringIndex]!;
    for (let segment = 0; segment < segments; segment += 1) {
      const angle = (segment / segments) * Math.PI * 2;
      // Ten restrained vertical folds broaden toward the hem. Two slower
      // harmonics keep the outline from becoming a mechanically perfect cone.
      const pleat = 1
        + Math.cos(angle * 10 + 0.28) * ring.fold
        + Math.sin(angle * 3 - 0.6) * ring.fold * 0.32;
      const hem = ringIndex === rings.length - 1
        ? Math.sin(angle * 5 + 0.4) * 0.018 + Math.sin(angle * 2.3) * 0.012
        : 0;
      positions.push(Math.cos(angle) * ring.rx * pleat, ring.y + hem, Math.sin(angle) * ring.rz * pleat);
    }
  }
  for (let ringIndex = 0; ringIndex < rings.length - 1; ringIndex += 1) {
    for (let segment = 0; segment < segments; segment += 1) {
      const next = (segment + 1) % segments;
      // Open a modest right-side walking slit only in the lowest panel. The
      // garment remains one continuous mesh; no detached flat falls are used.
      const sideAngle = Math.min(segment, segments - segment);
      if (ringIndex === rings.length - 2 && sideAngle <= 1) continue;
      const a = ringIndex * segments + segment;
      const b = ringIndex * segments + next;
      const c = (ringIndex + 1) * segments + next;
      const d = (ringIndex + 1) * segments + segment;
      indices.push(a, d, b, b, d, c);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function shaped(geometry: THREE.BufferGeometry, source: THREE.Material, castShadow = true): THREE.Mesh {
  const result = new THREE.Mesh(geometry, source);
  result.castShadow = castShadow;
  result.receiveShadow = true;
  return result;
}

/**
 * A deliberately authored, culturally grounded low-poly figure. These rigs use
 * ordinary first-century tunics, mantles, head cloths and sandals rather than
 * the fantasy RPG silhouettes formerly loaded for every speaking role.
 */
export class CharacterRig extends THREE.Group {
  readonly spec: CharacterSpec;
  private readonly torsoPivot = new THREE.Group();
  private readonly headPivot = new THREE.Group();
  private readonly leftArm = new THREE.Group();
  private readonly rightArm = new THREE.Group();
  private readonly leftElbow = new THREE.Group();
  private readonly rightElbow = new THREE.Group();
  private readonly leftLeg = new THREE.Group();
  private readonly rightLeg = new THREE.Group();
  private readonly leftKnee = new THREE.Group();
  private readonly rightKnee = new THREE.Group();
  private readonly eyes: THREE.Mesh[] = [];
  private readonly brows: THREE.Mesh[] = [];
  private readonly palmWounds: THREE.Mesh[] = [];
  private readonly garments: THREE.Object3D[] = [];
  private readonly crucifixionBody = new THREE.Group();
  private readonly woundMarks = new THREE.Group();
  private proceduralNeck?: THREE.Mesh;
  private readonly proceduralFallback: THREE.Object3D[] = [];
  private readonly profile: CostumeProfile;
  private heroRoot?: THREE.Group;
  private heroLowerRobe?: THREE.Group;
  private heroUpperRobe?: THREE.Group;
  private heroBones = new Map<string, HeroBoneState>();
  private heroHeadMeshes: THREE.Mesh[] = [];
  private heroCostumeMeshes: THREE.Mesh[] = [];
  private heroDetailsOnly = false;
  private crucifixionFullBodyRoot?: THREE.Group;
  private crucifixionFullBodyBones = new Map<string, HeroBoneState>();
  private crucifixionDistress?: THREE.Group;
  private crucifixionHeadRoot?: THREE.Group;
  private crucifixionHeadBone?: HeroBoneState;
  private retainedWounds = false;
  private pose: CharacterPose;

  constructor(spec: CharacterSpec) {
    super();
    this.spec = spec;
    this.pose = spec.pose ?? 'still';
    this.profile = CharacterRig.profileFor(spec);
    this.name = `character:${spec.id}`;
    this.position.copy(vec3(spec.position));
    this.rotation.y = spec.rotationY ?? 0;
    this.scale.setScalar(spec.scale ?? 1);

    this.buildBody();
    this.buildCrucifixionBody();
    this.buildHead();
    this.buildArm(this.leftArm, this.leftElbow, -1);
    this.buildArm(this.rightArm, this.rightElbow, 1);
    this.buildLeg(this.leftLeg, this.leftKnee, -1);
    this.buildLeg(this.rightLeg, this.rightKnee, 1);
    this.add(this.leftArm, this.rightArm, this.leftLeg, this.rightLeg, this.crucifixionBody, this.woundMarks);
    this.proceduralFallback.push(...this.children);
    this.applyPose(this.pose, 0);
  }

  /**
   * Opt-in detailed hero underlayer. The authored procedural figure remains in
   * memory and is restored automatically for unsupported poses such as the
   * crucifixion, so a failed asset load never removes a character.
   */
  attachUniversalHero(model: THREE.Group, costumeIncluded = false, detailsOnly = false): void {
    if (!['jesus', 'mary', 'peter', 'judas', 'pilate', 'disciple', 'witness'].includes(this.spec.role)) return;
    this.heroRoot?.removeFromParent();
    this.heroBones.clear();
    this.heroHeadMeshes = [];
    this.heroCostumeMeshes = [];
    this.heroDetailsOnly = detailsOnly;

    const hero = new THREE.Group();
    hero.name = `hero-presentation:${this.spec.id}`;
    hero.add(model);
    if (!costumeIncluded) hero.add(this.buildHeroCostume());
    if (costumeIncluded && !detailsOnly) {
      this.heroLowerRobe = this.buildHeroLowerRobe();
      hero.add(this.heroLowerRobe);
      if (this.spec.role === 'mary') {
        this.heroUpperRobe = this.buildClosedUpperTunic();
        hero.add(this.heroUpperRobe);
      }
    }
    this.add(hero);
    this.heroRoot = hero;

    for (const name of ['upperarm_l', 'upperarm_r', 'lowerarm_l', 'lowerarm_r', 'hand_l', 'hand_r', 'spine_03', 'Head']) {
      const bone = model.getObjectByName(name);
      if (bone) this.heroBones.set(name, { bone, rest: bone.quaternion.clone() });
    }
    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      // The source female bodice has a pronounced fantasy hourglass profile.
      // Retain its correctly skinned sleeves and hands, but let the closed
      // woven shell provide Mary's restrained first-century silhouette.
      if (this.spec.role === 'mary' && /^Female_Peasant_Body$/i.test(child.name)) {
        child.visible = false;
        return;
      }
      // The combined export preserves peasant mesh names. Everything else is
      // the detailed face, eyes, brows or hair imported from the head asset.
      if (/^(Male|Female)_Peasant_/i.test(child.name)) this.heroCostumeMeshes.push(child);
      else this.heroHeadMeshes.push(child);
    });
    if (!costumeIncluded) this.buildHeroSleeves();
    this.applyHeroPose(0);
  }

  /** Dedicated continuous-body presentation for Jesus on the central cross. */
  attachCrucifixionBody(model: THREE.Group): void {
    if (this.spec.role !== 'jesus') return;
    this.crucifixionFullBodyRoot?.removeFromParent();
    this.crucifixionFullBodyBones.clear();
    const root = new THREE.Group();
    root.name = `crucifixion-full-body:${this.spec.id}`;
    root.visible = false;
    root.add(model);

    // Restrained, non-graphic injuries are separate meshes so the released
    // shared-rig body remains continuous. Their very shallow profile keeps
    // them reading as dried abrasions rather than floating ornaments.
    const distress = new THREE.Group();
    distress.name = `crucifixion-distress:${this.spec.id}`;
    const driedBlood = new THREE.MeshStandardMaterial({
      color: 0x4d201d,
      roughness: 1,
      metalness: 0,
      transparent: true,
      opacity: 0.82,
      depthWrite: true,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const sideShape = new THREE.Shape();
    sideShape.moveTo(-0.016, -0.055);
    sideShape.lineTo(0.012, -0.043);
    sideShape.lineTo(0.025, -0.012);
    sideShape.lineTo(0.018, 0.034);
    sideShape.lineTo(-0.006, 0.061);
    sideShape.lineTo(-0.027, 0.028);
    sideShape.lineTo(-0.023, -0.018);
    sideShape.closePath();
    const side = shaped(new THREE.ShapeGeometry(sideShape, 3), driedBlood, false);
    side.name = 'wound:crucifixion-side';
    // The ribcage curves away on Jesus's right. Rotating this paper-thin
    // ellipsoid onto that tangent and embedding its back half eliminates the
    // floating-bead read in both the frontal and suspension-oblique cameras.
    side.position.set(0.205, 1.42, 0.153);
    side.rotation.set(0, -0.38, -0.38);
    const sideTrace = shaped(new THREE.BoxGeometry(0.013, 0.12, 0.003, 1, 4, 1), driedBlood, false);
    sideTrace.name = 'wound:crucifixion-side-trace';
    sideTrace.position.set(0.19, 1.305, 0.151);
    sideTrace.rotation.set(0, -0.34, -0.07);
    const shoulderAbrasion = shaped(new THREE.BoxGeometry(0.012, 0.095, 0.003, 1, 3, 1), driedBlood, false);
    shoulderAbrasion.name = 'wound:crucifixion-shoulder-abrasion';
    shoulderAbrasion.position.set(-0.17, 1.63, 0.151);
    shoulderAbrasion.rotation.set(0, 0.22, 0.82);
    distress.add(side, sideTrace, shoulderAbrasion);
    root.add(distress);
    this.crucifixionDistress = distress;
    this.add(root);
    this.crucifixionFullBodyRoot = root;
    for (const name of [
      'Head', 'neck_01', 'spine_01', 'spine_02', 'spine_03',
      'clavicle_l', 'clavicle_r', 'upperarm_l', 'upperarm_r',
      'lowerarm_l', 'lowerarm_r', 'hand_l', 'hand_r',
    ]) {
      const bone = model.getObjectByName(name);
      if (bone) this.crucifixionFullBodyBones.set(name, { bone, rest: bone.quaternion.clone() });
    }

    // Palm marks follow the skinned hands exactly through the final sag. They
    // are deliberately small, matte and irregular: legible in a hero close-up
    // without turning the scene into an anatomical effects showcase.
    for (const name of ['hand_l', 'hand_r']) {
      const hand = this.crucifixionFullBodyBones.get(name)?.bone;
      if (!hand) continue;
      const palm = shaped(new THREE.SphereGeometry(0.026, 10, 7), driedBlood.clone(), false);
      palm.name = `wound:crucifixion-${name}`;
      palm.position.set(0, -0.075, 0.021);
      palm.scale.set(0.7, 1.05, 0.16);
      hand.add(palm);
      const fasteningMaterial = new THREE.MeshStandardMaterial({ color: 0x574335, roughness: 1, metalness: 0 });
      for (let turn = 0; turn < 2; turn += 1) {
        const binding = shaped(new THREE.CylinderGeometry(0.034, 0.034, 0.009, 12, 1, true), fasteningMaterial, false);
        binding.name = `fastening:crucifixion-${name}:${turn}`;
        binding.position.y = -0.008 - turn * 0.014;
        binding.scale.set(1, 1, 0.82);
        hand.add(binding);
      }
    }
    this.applyHeroPose(0);
  }

  /** World-space palm anchor for deterministic scene props and wound inserts. */
  getWorldPalm(side: 'left' | 'right', target = new THREE.Vector3()): THREE.Vector3 {
    this.updateWorldMatrix(true, true);
    const crucifixionHand = this.pose === 'cruciform'
      ? this.crucifixionFullBodyBones.get(side === 'right' ? 'hand_r' : 'hand_l')?.bone
      : undefined;
    if (crucifixionHand) {
      crucifixionHand.updateWorldMatrix(true, false);
      return crucifixionHand.localToWorld(target.set(0, -0.075, 0.018));
    }
    const heroHand = this.heroBones.get(side === 'right' ? 'hand_r' : 'hand_l')?.bone;
    if (heroHand) {
      heroHand.updateWorldMatrix(true, false);
      return heroHand.localToWorld(target.set(0, -0.075, 0.018));
    }
    const elbow = side === 'right' ? this.rightElbow : this.leftElbow;
    return elbow.localToWorld(target.set(0, -0.47, 0.052));
  }

  attachCrucifixionHead(model: THREE.Group): void {
    this.crucifixionHeadRoot?.removeFromParent();
    const root = new THREE.Group();
    root.name = `crucifixion-head:${this.spec.id}`;
    root.visible = false;
    // The extracted Universal Base head keeps the source skeleton's 2.1 m
    // coordinates and is then uniformly scaled by the asset loader. Re-anchor
    // that scaled eye line to the authored crucifixion body's head pivot. The
    // small forward offset prevents the face being swallowed by the cross
    // upright without changing any of the corrected cruciform anatomy.
    model.position.set(0, -0.39, 0.075);
    root.add(model);
    this.add(root);
    this.crucifixionHeadRoot = root;
    const bone = model.getObjectByName('Head');
    this.crucifixionHeadBone = bone ? { bone, rest: bone.quaternion.clone() } : undefined;
  }

  setPose(pose: CharacterPose): void {
    this.pose = pose;
  }

  setRetainedWounds(visible: boolean): void {
    this.retainedWounds = visible;
  }

  /** Apply an eyeline after scene-level blocking has placed the full rig. */
  orientHeadToward(lookTarget: THREE.Vector3): void {
    const local = this.worldToLocal(lookTarget.clone());
    const yaw = Math.atan2(local.x, local.z);
    const pitch = -Math.atan2(local.y - 2.04, Math.hypot(local.x, local.z));
    this.headPivot.rotation.y += THREE.MathUtils.clamp(yaw, -0.42, 0.42);
    this.headPivot.rotation.x += THREE.MathUtils.clamp(pitch, -0.22, 0.22);
  }

  evaluate(time: number, lookTarget?: THREE.Vector3): void {
    this.applyPose(this.pose, time);
    this.applyHeroPose(time);
    const breath = harmonicNoise(time, this.spec.id.length * 13, 0.42) * 0.007;
    this.torsoPivot.scale.y = 1 + breath;

    // Short deterministic blinks keep close shots alive without lip-sync noise.
    const deathFall = this.pose === 'cruciform' ? THREE.MathUtils.smoothstep(time, 98, 106) : 0;
    const blink = this.pose === 'cruciform'
      ? THREE.MathUtils.lerp(0.48, 0.08, deathFall)
      : Math.sin(time * 0.83 + this.spec.id.length * 1.7) > 0.992 ? 0.16 : 1;
    for (const eye of this.eyes) eye.scale.y = blink;

    if (lookTarget) {
      this.orientHeadToward(lookTarget);
    } else if (this.pose === 'still' || this.pose === 'seated') {
      this.headPivot.rotation.y += harmonicNoise(time, this.spec.id.length * 29, 0.12) * 0.035;
    }
  }

  private buildHeroCostume(): THREE.Group {
    const costume = new THREE.Group();
    costume.name = `period-costume:${this.spec.id}`;
    const tunic = material(this.profile.tunic, 0.97);
    const mantle = material(this.profile.mantle, 0.98);
    const trim = material(this.profile.trim, 0.98);
    mantle.side = THREE.DoubleSide;

    // Full ankle-length wool tunic conceals the modern base texture and the
    // male pack's exaggerated torso, leaving only face, hands, and feet read.
    const skirt = shaped(robeGeometry(0.31, this.spec.role === 'mary' ? 0.48 : 0.44, 1.18), tunic);
    skirt.position.y = 0.67;
    costume.add(skirt);
    const upper = shaped(new THREE.CylinderGeometry(
      this.spec.role === 'mary' ? 0.28 : 0.29,
      this.spec.role === 'mary' ? 0.34 : 0.35,
      0.72,
      28,
      7,
    ), tunic);
    upper.position.y = 1.43;
    upper.scale.z = 0.72;
    costume.add(upper);
    const belt = shaped(new THREE.CylinderGeometry(0.34, 0.35, 0.055, 24), trim);
    belt.position.y = 1.08;
    costume.add(belt);

    // A narrow mantle hangs behind the body. Keeping it out of the arm plane
    // avoids the rigid triangular intersections seen in the first POC.
    const hanging = shaped(drapeGeometry(0.34, 1.02), mantle);
    hanging.position.set(this.spec.role === 'mary' ? -0.18 : 0.19, 1.2, -0.17);
    hanging.rotation.z = this.spec.role === 'mary' ? 0.06 : -0.06;
    hanging.rotation.y = Math.PI;
    costume.add(hanging);

    return costume;
  }

  private buildHeroLowerRobe(): THREE.Group {
    const lower = new THREE.Group();
    lower.name = `period-lower-tunic:${this.spec.id}`;
    // Male hero assets already carry a coherent knee-length tunic and skinned
    // legs. Extra unskinned panels became rigid boards during seated/reaching
    // poses, so only Mary needs a runtime lower garment replacement.
    if (this.spec.role !== 'mary') return lower;
    const wool = wovenClothMaterial(this.profile.tunic);
    wool.side = THREE.DoubleSide;
    const skirt = shaped(marySkirtGeometry(), wool);
    skirt.name = 'mary:folded-a-line-skirt';
    lower.add(skirt);
    return lower;
  }

  private buildClosedUpperTunic(): THREE.Group {
    const upper = new THREE.Group();
    upper.name = `period-upper-tunic:${this.spec.id}`;
    const wool = wovenClothMaterial(this.profile.tunic);
    wool.side = THREE.DoubleSide;

    // The elliptical ring shell follows a human shoulder/rib/waist profile
    // while remaining modest and closed. Correct outward triangle winding is
    // essential here: unlike a LatheGeometry tube, the X/Z radii can narrow
    // independently through the waist and preserve a readable torso in
    // profile as well as frontal shots.
    const torso = shaped(tunicShellGeometry(true), wool);
    torso.name = `${this.spec.id}:closed-upper-tunic`;
    torso.scale.z = 0.9;
    upper.add(torso);
    const sashMaterial = wovenClothMaterial(this.profile.mantle);
    const sash = shaped(new THREE.CylinderGeometry(0.208, 0.21, 0.045, 28, 2), sashMaterial);
    sash.name = 'mary:woven-waist-sash';
    sash.position.y = 1.065;
    sash.scale.z = 0.68;
    upper.add(sash);
    const neckline = shaped(new THREE.TorusGeometry(0.096, 0.009, 8, 32), sashMaterial, false);
    neckline.name = 'mary:woven-neck-binding';
    neckline.position.y = 1.715;
    neckline.rotation.x = Math.PI / 2;
    neckline.scale.z = 0.76;
    upper.add(neckline);
    return upper;
  }

  private buildHeroSleeves(): void {
    const cloth = material(this.profile.tunic, 0.97);
    for (const name of ['upperarm_l', 'upperarm_r']) {
      const state = this.heroBones.get(name);
      if (!state) continue;
      // These sleeves live in bone-local space, so the fabric follows every
      // blessing/reaching pose instead of remaining as detached vertical rods.
      const sleeve = shaped(new THREE.CapsuleGeometry(0.088, 0.1, 6, 14), cloth);
      sleeve.position.y = 0.09;
      sleeve.scale.set(1, 1.08, 0.92);
      state.bone.add(sleeve);
    }
  }

  private applyHeroPose(time: number): void {
    if (!this.heroRoot && !this.crucifixionFullBodyRoot) return;
    const crucified = this.pose === 'cruciform' && this.spec.role === 'jesus';
    const useCrucifixionFullBody = crucified && Boolean(this.crucifixionFullBodyRoot);
    const detailedCrucifixionHead = crucified && !useCrucifixionFullBody && !this.heroDetailsOnly && this.heroHeadMeshes.length > 0;
    const detailOnlyPose = this.pose === 'still' || this.pose === 'reaching' || this.pose === 'blessing' || this.pose === 'mourning';
    const useHero = !crucified && (!this.heroDetailsOnly || detailOnlyPose);
    const crucifixionDeath = crucified ? THREE.MathUtils.smoothstep(time, 98, 106) : 0;
    const crucifixionFatigue = crucified ? THREE.MathUtils.smoothstep(time, 26, 88) : 0;
    const strainedBreath = crucified
      ? (1 - crucifixionDeath) * Math.sin(time * 1.04) * (0.0045 - crucifixionFatigue * 0.0015)
      : 0;
    // Only the imported head/hair remains visible in this pose. Lift its
    // shared-rig origin so the jaw clears the procedural clavicle instead of
    // sitting inside the chest; standing poses retain the untouched origin.
    if (this.heroRoot) {
      this.heroRoot.position.set(0, 0, 0);
      this.heroRoot.visible = useHero || detailedCrucifixionHead;
    }
    for (const mesh of this.heroHeadMeshes) mesh.visible = useHero || detailedCrucifixionHead;
    for (const mesh of this.heroCostumeMeshes) mesh.visible = useHero;
    if (this.heroLowerRobe) this.heroLowerRobe.visible = useHero;
    if (this.heroUpperRobe) this.heroUpperRobe.visible = useHero;
    if (this.crucifixionHeadRoot) this.crucifixionHeadRoot.visible = crucified && !useCrucifixionFullBody;
    if (this.crucifixionFullBodyRoot) {
      this.crucifixionFullBodyRoot.visible = useCrucifixionFullBody;
      // Keep the continuous arm mesh just in front of the beam's camera-facing
      // surface. At the old coplanar depth the timber occluded both forearms
      // while the hands remained visible, creating a false detached-hand gap.
      this.crucifixionFullBodyRoot.position.set(
        0,
        -0.035 - crucifixionFatigue * 0.045 - crucifixionDeath * 0.055 + strainedBreath,
        0.12,
      );
      this.crucifixionFullBodyRoot.rotation.set(
        0.015 + crucifixionFatigue * 0.018,
        0,
        crucifixionFatigue * 0.025 + crucifixionDeath * 0.025,
      );
    }
    if (this.crucifixionDistress) this.crucifixionDistress.visible = useCrucifixionFullBody;
    if (this.heroDetailsOnly) {
      // Keep the coherent authored body/costume and replace only its low-detail
      // face. This deliberately avoids mixing imported superhero anatomy with
      // the first-century garment rig.
      this.headPivot.visible = !useHero;
    } else {
      for (const child of this.proceduralFallback) child.visible = !useHero && !useCrucifixionFullBody;
      if (!useHero && !useCrucifixionFullBody) {
        this.crucifixionBody.visible = crucified;
        this.woundMarks.visible = crucified;
      }
    }
    if (useCrucifixionFullBody) {
      this.headPivot.visible = false;
      if (this.proceduralNeck) this.proceduralNeck.visible = false;
      for (const state of this.crucifixionFullBodyBones.values()) state.bone.quaternion.copy(state.rest);
      const head = this.crucifixionFullBodyBones.get('Head');
      if (head) {
        // The lowered eyeline, asymmetric tilt, and increasingly collapsed
        // neck make the face read as exhausted well before the final breath.
        // A faint breath-led movement remains until death, avoiding a neutral
        // mannequin pose while keeping the performance restrained.
        head.bone.rotateX(0.12 + crucifixionFatigue * 0.2 + crucifixionDeath * 0.38 - strainedBreath * 2.1);
        head.bone.rotateY(0.025 + crucifixionFatigue * 0.025);
        head.bone.rotateZ(0.055 + crucifixionFatigue * 0.09 + crucifixionDeath * 0.13);
      }
      const neck = this.crucifixionFullBodyBones.get('neck_01');
      neck?.bone.rotateX(0.045 + crucifixionFatigue * 0.08 + crucifixionDeath * 0.13);
      neck?.bone.rotateZ(crucifixionFatigue * 0.035 + crucifixionDeath * 0.045);
      const lowerSpine = this.crucifixionFullBodyBones.get('spine_01');
      lowerSpine?.bone.rotateX(crucifixionFatigue * 0.025 + strainedBreath * 0.8);
      const middleSpine = this.crucifixionFullBodyBones.get('spine_02');
      middleSpine?.bone.rotateX(crucifixionFatigue * 0.035 + strainedBreath * 1.15);
      middleSpine?.bone.rotateZ(crucifixionFatigue * 0.018);
      const upperSpine = this.crucifixionFullBodyBones.get('spine_03');
      upperSpine?.bone.rotateX(crucifixionFatigue * 0.045 + strainedBreath * 1.4);
      upperSpine?.bone.rotateZ(crucifixionFatigue * 0.025);
      // Let the shoulders bear visibly against the fixed hands. These values
      // produce a shallow inverted arc from each fixed palm toward the chest,
      // rather than the earlier horizontal T-pose. Forearm compensation keeps
      // the wrists within the thickness of the timber as the elbows descend.
      const suspension = THREE.MathUtils.lerp(0.72, 1, crucifixionFatigue);
      this.crucifixionFullBodyBones.get('clavicle_l')?.bone.rotateZ(0.04 * suspension);
      this.crucifixionFullBodyBones.get('clavicle_r')?.bone.rotateZ(-0.08 * suspension);
      this.crucifixionFullBodyBones.get('upperarm_l')?.bone.rotateZ(0.09 * suspension);
      this.crucifixionFullBodyBones.get('upperarm_r')?.bone.rotateZ(-0.17 * suspension);
      this.crucifixionFullBodyBones.get('lowerarm_l')?.bone.rotateZ(0.02 * suspension);
      this.crucifixionFullBodyBones.get('lowerarm_r')?.bone.rotateZ(-0.05 * suspension);
    } else if (detailedCrucifixionHead) {
      this.headPivot.visible = false;
      if (this.proceduralNeck) this.proceduralNeck.visible = false;
      for (const state of this.heroBones.values()) state.bone.quaternion.copy(state.rest);
      const detailedHead = this.heroBones.get('Head');
      if (detailedHead) {
        detailedHead.bone.rotateX(0.3 + crucifixionDeath * 0.2);
        detailedHead.bone.rotateY(0.04);
        detailedHead.bone.rotateZ(0.1 + crucifixionDeath * 0.08);
      }
    } else if (crucified && this.crucifixionHeadRoot) {
      this.headPivot.visible = false;
      if (this.crucifixionHeadBone) {
        this.crucifixionHeadBone.bone.quaternion.copy(this.crucifixionHeadBone.rest);
        this.crucifixionHeadBone.bone.rotateX(0.3);
        this.crucifixionHeadBone.bone.rotateZ(0.1);
      }
    } else if (!useHero) {
      this.headPivot.visible = true;
      if (this.proceduralNeck) this.proceduralNeck.visible = true;
    }
    if (!useHero) return;

    for (const state of this.heroBones.values()) state.bone.quaternion.copy(state.rest);
    if (this.heroLowerRobe) {
      this.heroLowerRobe.position.set(0, 0, 0);
      this.heroLowerRobe.rotation.set(0, 0, 0);
      this.heroLowerRobe.scale.set(1, 1, 1);
      if (this.pose === 'seated') {
        this.heroLowerRobe.position.y = 0.43;
        this.heroLowerRobe.scale.y = 0.62;
      } else if (this.pose === 'kneeling') {
        this.heroLowerRobe.position.y = 0.47;
        this.heroLowerRobe.scale.y = 0.58;
      }
    }
    this.updateMatrixWorld(true);
    const rootWorld = this.getWorldQuaternion(new THREE.Quaternion());
    const orient = (name: string, direction: THREE.Vector3) => {
      const state = this.heroBones.get(name);
      if (!state || !state.bone.parent) return;
      state.bone.parent.updateMatrixWorld(true);
      const parentWorld = state.bone.parent.getWorldQuaternion(new THREE.Quaternion());
      const parentInverse = parentWorld.invert();
      const desired = direction.clone().normalize().applyQuaternion(rootWorld).applyQuaternion(parentInverse).normalize();
      state.bone.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), desired);
    };
    orient('upperarm_l', new THREE.Vector3(-0.08, -1, 0.04));
    orient('lowerarm_l', new THREE.Vector3(-0.03, -1, 0.08));
    orient('upperarm_r', new THREE.Vector3(0.08, -1, 0.04));
    orient('lowerarm_r', new THREE.Vector3(0.03, -1, 0.08));
    if (this.pose === 'walking') {
      const stride = Math.sin(time * 2.25 + this.spec.id.length) * 0.38;
      orient('upperarm_l', new THREE.Vector3(-0.08, -0.94, stride));
      orient('lowerarm_l', new THREE.Vector3(-0.03, -0.98, stride * 0.35));
      orient('upperarm_r', new THREE.Vector3(0.08, -0.94, -stride));
      orient('lowerarm_r', new THREE.Vector3(0.03, -0.98, -stride * 0.35));
    } else if (this.pose === 'burdened') {
      // Hands rise outward and back toward the patibulum while the scene root
      // supplies the forward lean. This keeps the timber's weight legible
      // without turning the road into a cruciform pose.
      orient('upperarm_l', new THREE.Vector3(-0.44, -0.82, -0.36));
      orient('lowerarm_l', new THREE.Vector3(0.3, 0.86, -0.42));
      orient('upperarm_r', new THREE.Vector3(0.44, -0.82, -0.36));
      orient('lowerarm_r', new THREE.Vector3(-0.3, 0.86, -0.42));
    } else if (this.pose === 'bound') {
      orient('upperarm_l', new THREE.Vector3(-0.12, -0.82, -0.56));
      orient('lowerarm_l', new THREE.Vector3(0.16, -0.35, -0.92));
      orient('upperarm_r', new THREE.Vector3(0.12, -0.82, -0.56));
      orient('lowerarm_r', new THREE.Vector3(-0.16, -0.35, -0.92));
    } else if (this.pose === 'seated') {
      orient('upperarm_l', new THREE.Vector3(-0.12, -0.68, 0.72));
      orient('lowerarm_l', new THREE.Vector3(-0.04, -0.2, 0.98));
      orient('upperarm_r', new THREE.Vector3(0.12, -0.68, 0.72));
      orient('lowerarm_r', new THREE.Vector3(0.04, -0.2, 0.98));
    } else if (this.pose === 'kneeling') {
      orient('upperarm_l', new THREE.Vector3(-0.1, -0.72, 0.68));
      orient('lowerarm_l', new THREE.Vector3(-0.03, -0.3, 0.95));
      orient('upperarm_r', new THREE.Vector3(0.1, -0.72, 0.68));
      orient('lowerarm_r', new THREE.Vector3(0.03, -0.3, 0.95));
    } else if (this.pose === 'reaching' || this.pose === 'blessing') {
      orient('upperarm_r', this.pose === 'reaching'
        ? new THREE.Vector3(0.2, -0.78, 0.58)
        : new THREE.Vector3(0.2, -0.72, 0.66));
      orient('lowerarm_r', this.pose === 'reaching'
        ? new THREE.Vector3(0.08, -0.28, 0.96)
        : new THREE.Vector3(0.05, -0.25, 0.97));
    }
    const head = this.heroBones.get('Head');
    if (head) {
      if (this.pose === 'mourning') head.bone.rotateX(0.28);
      else if (this.pose === 'kneeling') head.bone.rotateX(0.2);
      else if (this.pose === 'bound' || this.pose === 'burdened') head.bone.rotateX(0.1);
      else if (this.pose === 'reaching') head.bone.rotateY(-0.12);
    }
    const spine = this.heroBones.get('spine_03');
    if (spine && (this.pose === 'kneeling' || this.pose === 'mourning')) spine.bone.rotateX(0.1);
  }

  private buildBody(): void {
    const { tunic, mantle, trim, width, longRobe } = this.profile;
    const tunicMaterial = material(tunic);
    const mantleMaterial = material(mantle, 0.94);
    const trimMaterial = material(trim, 0.96);
    const skinMaterial = material(this.profile.skin, 0.86);

    const lowerHeight = longRobe ? 1.14 : 0.92;
    const lowerBottom = width * (longRobe ? 1.22 : 1.04);
    const lower = longRobe
      ? createPeriodLowerGarment({ tunic, trim: mantle, longRobe: true })
      : shaped(robeGeometry(width * 0.72, lowerBottom, lowerHeight), tunicMaterial);
    if (!longRobe) lower.position.y = 0.81;
    this.add(lower);
    this.garments.push(lower);

    const torso = shaped(new THREE.CapsuleGeometry(width * 0.72, 0.39, 6, 12), tunicMaterial);
    torso.position.y = 1.48;
    torso.scale.set(1.02, 1, 0.72);
    this.torsoPivot.add(torso);
    this.garments.push(torso);

    const neck = shaped(new THREE.CylinderGeometry(0.105, 0.12, 0.2, 9), skinMaterial);
    neck.name = `anatomy:neck:${this.spec.id}`;
    neck.position.y = 1.88;
    this.proceduralNeck = neck;
    this.torsoPivot.add(neck);

    // A woven mantle crosses one shoulder, with a separate hanging fold. The
    // asymmetry creates role-readable silhouettes without fantasy armour.
    const drapeSide = this.spec.role === 'mary' ? -1 : 1;
    const shoulder = shaped(new THREE.SphereGeometry(width * 0.58, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.53), mantleMaterial);
    shoulder.scale.set(1.28, 0.36, 0.8);
    shoulder.position.set(drapeSide * width * 0.43, 1.72, -0.005);
    shoulder.rotation.z = this.spec.role === 'mary' ? 0.08 : -0.08;
    this.torsoPivot.add(shoulder);
    this.garments.push(shoulder);

    mantleMaterial.side = THREE.DoubleSide;
    const drape = shaped(drapeGeometry(width * 0.72, longRobe ? 1.02 : 0.78), mantleMaterial);
    drape.position.set(drapeSide * width * 0.48, 1.2, 0.285);
    drape.rotation.z = drapeSide * -0.045;
    this.torsoPivot.add(drape);
    this.garments.push(drape);

    const belt = shaped(new THREE.CylinderGeometry(width * 0.86, width * 0.88, 0.072, 18), trimMaterial);
    belt.position.y = 1.11;
    this.add(belt);
    this.garments.push(belt);

    const foldMaterial = material(new THREE.Color(tunic).multiplyScalar(0.78).getHex(), 0.97);
    const foldLength = longRobe ? 0.78 : 0.57;
    for (const offset of [-0.16, 0, 0.16]) {
      const fold = shaped(new THREE.CapsuleGeometry(0.013, foldLength, 3, 6), foldMaterial, false);
      fold.position.set(offset, longRobe ? 0.67 : 0.8, lowerBottom * 0.93 - Math.abs(offset) * 0.06);
      fold.scale.x = 0.7;
      this.add(fold);
      this.garments.push(fold);
    }
    const hem = shaped(new THREE.TorusGeometry(lowerBottom * 0.97, 0.017, 5, 24), trimMaterial);
    hem.rotation.x = Math.PI / 2;
    hem.position.y = longRobe ? 0.12 : 0.35;
    this.add(hem);
    this.garments.push(hem);

    const tie = shaped(drapeGeometry(0.12, 0.42), trimMaterial);
    trimMaterial.side = THREE.DoubleSide;
    tie.position.set(0.11, 0.93, 0.3);
    tie.rotation.z = -0.1;
    this.add(tie);
    this.garments.push(tie);

    if (this.spec.role === 'pilate' || this.spec.role === 'soldier') this.buildRomanDetails();
    // Rotate posture from the waist, not from the ground plane.
    const waistPivot = 1.08;
    for (const child of this.torsoPivot.children) child.position.y -= waistPivot;
    this.torsoPivot.position.y = waistPivot;
    this.add(this.torsoPivot);
  }

  private buildCrucifixionBody(): void {
    this.crucifixionBody.name = `costume:crucifixion:${this.spec.id}`;
    this.crucifixionBody.visible = false;
    if (this.spec.role !== 'jesus') return;
    const skin = material(this.profile.skin, 0.88);
    const linen = material(0x9b8b72, 0.98);
    const torso = shaped(new THREE.CapsuleGeometry(0.265, 0.43, 7, 13), skin);
    torso.position.y = 1.47;
    torso.scale.set(1.04, 1.05, 0.72);
    this.crucifixionBody.add(torso);
    const cloth = shaped(new THREE.CylinderGeometry(0.29, 0.37, 0.48, 11, 3), linen);
    cloth.position.y = 1.02;
    this.crucifixionBody.add(cloth);
    const fold = shaped(new THREE.BoxGeometry(0.2, 0.45, 0.045, 2, 4, 1), material(0x887a66, 0.98));
    fold.position.set(0.07, 0.92, 0.275);
    fold.rotation.z = -0.06;
    const wound = shaped(new THREE.SphereGeometry(0.052, 10, 7), material(0x6a2926, 0.98), false);
    wound.name = 'wound:side';
    wound.scale.set(0.58, 1.35, 0.22);
    wound.position.set(0.22, 1.48, 0.205);
    wound.rotation.z = -0.42;
    const trace = shaped(new THREE.CapsuleGeometry(0.009, 0.12, 3, 6), material(0x5b2723, 0.99), false);
    trace.name = 'wound:side-trace';
    trace.position.set(0.21, 1.35, 0.2);
    trace.rotation.z = -0.08;
    this.crucifixionBody.add(fold, wound, trace);
  }

  private buildRomanDetails(): void {
    const second = this.spec.id.endsWith('2');
    const bronze = material(this.spec.role === 'pilate' ? 0x8c7350 : second ? 0x5f625d : 0x746653, 0.78);
    const border = material(this.spec.role === 'pilate' ? 0x7f2725 : second ? 0x6a5034 : 0x67342d, 0.92);
    const chest = shaped(new THREE.CylinderGeometry(this.profile.width * 0.61, this.profile.width * 0.7, 0.55, 18, 3, true), bronze);
    chest.scale.z = second ? 0.66 : 0.72;
    chest.position.set(0, 1.49, 0.03);
    this.torsoPivot.add(chest);
    if (this.spec.role === 'soldier') {
      const scarf = shaped(new THREE.TorusGeometry(this.profile.width * 0.39, 0.035, 6, 18), border);
      scarf.rotation.x = Math.PI / 2;
      scarf.position.set(0, 1.8, 0.015);
      scarf.scale.z = 0.78;
      this.torsoPivot.add(scarf);
      const strap = shaped(new THREE.BoxGeometry(0.065, 0.68, 0.035, 2, 5, 1), border);
      strap.position.set(second ? -0.08 : 0.08, 1.48, 0.225);
      strap.rotation.z = second ? -0.34 : 0.34;
      this.torsoPivot.add(strap);
      if (second) {
        // The secondary guard carries a short, weather-darkened shoulder
        // cloak. It breaks the interchangeable mannequin outline while
        // remaining plausible field clothing and safely clear of the spear.
        const cloakMaterial = material(0x353d3c, 0.98);
        cloakMaterial.side = THREE.DoubleSide;
        const shoulderCloak = shaped(drapeGeometry(0.3, 0.78), cloakMaterial);
        shoulderCloak.name = 'roman:secondary-shoulder-cloak';
        shoulderCloak.position.set(-0.27, 1.37, -0.16);
        shoulderCloak.rotation.set(0.02, Math.PI, -0.09);
        this.torsoPivot.add(shoulderCloak);
        const clasp = shaped(new THREE.CylinderGeometry(0.055, 0.055, 0.022, 12), bronze, false);
        clasp.name = 'roman:secondary-cloak-clasp';
        clasp.rotation.x = Math.PI / 2;
        clasp.position.set(-0.245, 1.72, 0.17);
        this.torsoPivot.add(clasp);
      }
    }
    const pteryges = new THREE.Group();
    for (let index = -2; index <= 2; index += 1) {
      const strip = shaped(new THREE.BoxGeometry(0.105, 0.42 - Math.abs(index) * 0.025, 0.055), border);
      strip.position.set(index * 0.105, 1.04, 0.18);
      strip.rotation.z = index * -0.035;
      pteryges.add(strip);
    }
    this.torsoPivot.add(pteryges);
  }

  private buildHead(): void {
    const skin = material(this.profile.skin, 0.84);
    const hair = material(this.profile.hair, 0.96);
    const dark = material(0x201915, 0.98);
    const head = shaped(new THREE.SphereGeometry(0.222, 18, 14), skin);
    const headShape: Record<string, [number, number, number]> = {
      jesus: [0.82, 1.06, 0.91],
      mary: [0.84, 1.03, 0.9],
      peter: [0.91, 0.98, 0.93],
      judas: [0.82, 1.04, 0.88],
      pilate: [0.87, 1, 0.91],
      soldier: this.spec.id.endsWith('2') ? [0.9, 0.98, 0.92] : [0.85, 1.03, 0.89],
      disciple: this.spec.id.endsWith('2') ? [0.86, 1.01, 0.9] : [0.9, 0.99, 0.92],
    };
    head.scale.set(...(headShape[this.spec.role] ?? [0.86, 1.01, 0.9]));
    head.position.y = 2.085;
    this.headPivot.add(head);

    for (const side of [-1, 1]) {
      const ear = shaped(new THREE.SphereGeometry(0.043, 9, 7), skin);
      ear.scale.set(0.6, 1.05, 0.5);
      ear.position.set(side * 0.205, 2.08, 0.01);
      this.headPivot.add(ear);

      const eye = shaped(new THREE.SphereGeometry(0.016, 10, 7), dark, false);
      eye.scale.set(1.38, 0.58, 0.42);
      eye.position.set(side * 0.069, 2.104, 0.216);
      const catchlight = shaped(new THREE.SphereGeometry(0.0032, 6, 5), material(0xd9c9a9, 0.9), false);
      catchlight.position.set(side * 0.069 - 0.004, 2.108, 0.226);
      this.eyes.push(eye);
      this.headPivot.add(eye, catchlight);

      const brow = shaped(new THREE.CapsuleGeometry(0.008, 0.058, 3, 6), hair, false);
      brow.position.set(side * 0.075, 2.155, 0.22);
      brow.rotation.z = Math.PI / 2 + side * -0.08;
      this.brows.push(brow);
      this.headPivot.add(brow);
    }

    const nose = shaped(new THREE.ConeGeometry(0.029, 0.105, 7), skin);
    nose.rotation.x = Math.PI / 2;
    nose.position.set(0, 2.065, 0.253);
    this.headPivot.add(nose);
    const mouth = shaped(new THREE.BoxGeometry(0.072, 0.012, 0.012), material(0x5f342e, 0.98), false);
    mouth.position.set(0, 2.005, 0.225);
    this.headPivot.add(mouth);

    const veiledWitness = this.spec.role === 'witness' && this.spec.id !== 'simon';
    if (this.spec.role === 'mary' || veiledWitness) this.buildHeadCloth();
    else if (this.spec.role === 'soldier') this.buildRomanHelmet();
    else this.buildHairAndBeard(hair);

    // Facial expression must rotate around the base of the skull. Keeping the
    // authored facial coordinates but recentering their parent prevents the
    // detached-head arc previously visible in crucifixion close-ups.
    const skullPivot = 1.91;
    for (const child of this.headPivot.children) child.position.y -= skullPivot;
    this.headPivot.position.y = skullPivot;
    this.add(this.headPivot);
  }

  private buildHeadCloth(): void {
    const cloth = material(this.profile.mantle, 0.97);
    const wrap = shaped(new THREE.SphereGeometry(0.255, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.68), cloth);
    wrap.scale.set(0.88, 1, 0.92);
    wrap.position.set(0, 2.15, -0.035);
    this.headPivot.add(wrap);
    for (const side of [-1, 1]) {
      cloth.side = THREE.DoubleSide;
      const fall = shaped(drapeGeometry(0.14, 0.54), cloth);
      fall.position.set(side * 0.205, 1.91, -0.1);
      fall.rotation.z = side * 0.08;
      this.headPivot.add(fall);
    }
  }

  private buildHairAndBeard(hair: THREE.Material): void {
    const short = this.spec.role === 'pilate' || this.spec.role === 'peter';
    const cap = shaped(new THREE.SphereGeometry(0.218, 18, 11, 0, Math.PI * 2, 0, Math.PI * (short ? 0.45 : 0.54)), hair);
    cap.position.set(0, 2.17, -0.042);
    cap.scale.set(0.88, 0.96, 0.9);
    this.headPivot.add(cap);
    if (!short) {
      for (let index = -3; index <= 3; index += 1) {
        const size = 0.026 + (Math.abs(index * 7 + this.spec.id.length) % 3) * 0.003;
        const curl = shaped(new THREE.SphereGeometry(size, 9, 7), hair);
        curl.scale.set(1.18, 0.72, 0.8);
        curl.position.set(index * 0.049, 2.175 - Math.abs(index % 3) * 0.008, 0.183 - Math.abs(index) * 0.003);
        this.headPivot.add(curl);
      }
    }
    if (this.spec.role === 'jesus') {
      for (const side of [-1, 1]) {
        for (let layer = 0; layer < 2; layer += 1) {
          const lock = shaped(new THREE.CapsuleGeometry(0.034 - layer * 0.003, 0.25 + layer * 0.08, 5, 8), hair);
          lock.position.set(side * (0.17 + layer * 0.035), 2.0 - layer * 0.075, -0.055 - layer * 0.035);
          lock.rotation.z = side * (0.07 + layer * 0.035);
          this.headPivot.add(lock);
        }
      }
    }
    const cleanShaven = this.spec.role === 'pilate'
      || this.spec.role === 'judas'
      || (this.spec.role === 'disciple' && this.spec.id.endsWith('2'));
    if (!cleanShaven) {
      const beard = shaped(new THREE.SphereGeometry(0.15, 15, 9, 0, Math.PI * 2, Math.PI * 0.32, Math.PI * 0.54), hair);
      beard.scale.set(
        this.spec.role === 'peter' ? 0.94 : 0.82,
        this.spec.role === 'jesus' ? 1.08 : this.spec.role === 'peter' ? 0.76 : 0.88,
        this.spec.role === 'peter' ? 0.74 : 0.68,
      );
      beard.position.set(0, 1.995, 0.105);
      this.headPivot.add(beard);
    }
  }

  private buildRomanHelmet(): void {
    const second = this.spec.id.endsWith('2');
    const bronze = material(second ? 0x655e55 : 0x75644c, 0.78);
    const cap = shaped(new THREE.SphereGeometry(0.235, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.53), bronze);
    cap.position.set(0, 2.185, -0.025);
    cap.scale.set(second ? 0.94 : 0.9, 0.9, 0.96);
    this.headPivot.add(cap);
    const browBand = shaped(new THREE.TorusGeometry(0.205, 0.018, 5, 16, Math.PI), bronze);
    browBand.rotation.set(Math.PI / 2, 0, Math.PI);
    browBand.position.set(0, 2.12, 0.045);
    this.headPivot.add(browBand);
    for (const side of [-1, 1]) {
      const guard = shaped(new THREE.BoxGeometry(0.052, second ? 0.18 : 0.21, 0.042, 2, 3, 1), bronze);
      guard.position.set(side * 0.19, 2.015, -0.005);
      guard.rotation.z = side * 0.08;
      this.headPivot.add(guard);
    }
  }

  private buildArm(shoulder: THREE.Group, elbow: THREE.Group, side: -1 | 1): void {
    const skin = material(this.profile.skin, 0.84);
    const cloth = this.spec.role === 'jesus' ? skin : material(this.profile.tunic);
    shoulder.position.set(side * this.profile.width * 0.82, 1.68, 0);
    const sleeveCap = shaped(new THREE.SphereGeometry(0.112, 12, 9), cloth);
    sleeveCap.scale.set(0.92, 1.08, 0.82);
    sleeveCap.position.y = -0.035;
    const upper = shaped(new THREE.CapsuleGeometry(0.088, 0.29, 5, 9), cloth);
    upper.position.y = -0.245;
    shoulder.add(sleeveCap, upper);
    elbow.position.y = -0.48;
    const forearm = shaped(new THREE.CapsuleGeometry(0.073, 0.27, 5, 9), skin);
    forearm.position.y = -0.22;
    elbow.add(forearm);
    const hand = shaped(new THREE.SphereGeometry(0.086, 12, 9), skin);
    hand.scale.set(0.64, 1.22, 0.44);
    hand.position.y = -0.47;
    const thumb = shaped(new THREE.CapsuleGeometry(0.022, 0.065, 3, 6), skin);
    thumb.position.set(side * -0.06, -0.455, 0.025);
    thumb.rotation.z = side * 0.65;
    elbow.add(hand, thumb);
    if (this.spec.role === 'jesus') {
      const palmWound = shaped(new THREE.SphereGeometry(0.023, 8, 6), material(0x6e2724, 0.96), false);
      palmWound.scale.set(0.72, 1, 0.35);
      palmWound.position.set(0, -0.47, 0.052);
      palmWound.visible = false;
      this.palmWounds.push(palmWound);
      elbow.add(palmWound);
    }
    shoulder.add(elbow);

    const wound = shaped(new THREE.TorusGeometry(0.078, 0.012, 5, 12), material(0x6e2724, 0.95), false);
    wound.rotation.x = Math.PI / 2;
    wound.position.set(side * 1.235, 1.68, 0.01);
    this.woundMarks.add(wound);
  }

  private buildLeg(hip: THREE.Group, knee: THREE.Group, side: -1 | 1): void {
    const skin = material(this.profile.skin, 0.86);
    const cloth = this.spec.role === 'jesus' ? skin : material(this.profile.tunic);
    const leather = material(0x3b2b21, 0.98);
    hip.position.set(side * 0.17, 0.72, 0);
    const thigh = shaped(new THREE.CapsuleGeometry(0.1, 0.34, 5, 9), cloth);
    thigh.position.y = -0.28;
    hip.add(thigh);
    knee.position.y = -0.55;
    const shin = shaped(new THREE.CapsuleGeometry(0.082, 0.32, 5, 9), skin);
    shin.position.y = -0.25;
    knee.add(shin);
    const sandal = shaped(new THREE.CapsuleGeometry(0.085, 0.17, 4, 8), leather);
    sandal.rotation.x = Math.PI / 2;
    sandal.scale.set(0.78, 1.15, 0.42);
    sandal.position.set(0, -0.52, 0.075);
    const strap = shaped(new THREE.TorusGeometry(0.082, 0.012, 4, 10, Math.PI), leather);
    strap.rotation.set(Math.PI / 2, 0, Math.PI / 2);
    strap.position.set(0, -0.48, 0.14);
    knee.add(sandal, strap);
    hip.add(knee);
  }

  private applyPose(pose: CharacterPose, time: number): void {
    this.position.set(this.spec.position.x, this.spec.position.y, this.spec.position.z);
    this.leftArm.rotation.set(0.025, 0, -0.07);
    this.rightArm.rotation.set(-0.02, 0, 0.045);
    this.leftElbow.rotation.set(-0.1, 0, -0.025);
    this.rightElbow.rotation.set(-0.08, 0, 0.035);
    this.leftArm.position.set(-this.profile.width * 0.82, 1.68, 0);
    this.rightArm.position.set(this.profile.width * 0.82, 1.68, 0);
    this.leftLeg.rotation.set(0, 0, 0);
    this.rightLeg.rotation.set(0, 0, 0);
    this.leftLeg.position.set(-0.17, 0.72, 0);
    this.rightLeg.position.set(0.17, 0.72, 0);
    this.leftKnee.rotation.set(0, 0, 0);
    this.rightKnee.rotation.set(0, 0, 0);
    this.torsoPivot.position.y = 1.08;
    this.torsoPivot.rotation.set(0, 0, 0);
    this.headPivot.rotation.set(0, 0, 0);
    this.headPivot.position.y = 1.91;
    this.crucifixionBody.position.set(0, 0, 0);
    this.crucifixionBody.rotation.set(0, 0, 0);
    this.crucifixionBody.scale.set(1, 1, 1);
    this.woundMarks.visible = false;
    const crucified = pose === 'cruciform' && this.spec.role === 'jesus';
    for (const garment of this.garments) garment.visible = !crucified;
    this.crucifixionBody.visible = crucified;
    for (const wound of this.palmWounds) wound.visible = crucified || this.retainedWounds;
    for (const brow of this.brows) brow.rotation.z = Math.PI / 2 + Math.sign(brow.position.x) * -0.08;

    switch (pose) {
      case 'seated':
        this.position.y -= 0.45;
        this.leftLeg.rotation.x = this.rightLeg.rotation.x = -1.22;
        this.leftKnee.rotation.x = this.rightKnee.rotation.x = 1.1;
        this.leftArm.rotation.x = this.rightArm.rotation.x = -0.42;
        this.leftElbow.rotation.x = this.rightElbow.rotation.x = -0.5;
        break;
      case 'walking': {
        const stride = Math.sin(time * 2.25 + this.spec.id.length) * 0.34;
        this.leftLeg.rotation.x = stride;
        this.rightLeg.rotation.x = -stride;
        this.leftArm.rotation.x = -stride * 0.72;
        this.rightArm.rotation.x = stride * 0.72;
        this.position.y += Math.abs(Math.sin(time * 2.25)) * 0.022;
        break;
      }
      case 'burdened':
        this.leftArm.rotation.set(0.58, 0, -0.82);
        this.rightArm.rotation.set(0.58, 0, 0.82);
        this.leftElbow.rotation.set(-0.95, 0, -0.16);
        this.rightElbow.rotation.set(-0.95, 0, 0.16);
        this.headPivot.rotation.x = 0.13;
        break;
      case 'supporting':
        this.leftArm.rotation.set(-1.05, 0, -0.16);
        this.rightArm.rotation.set(-1.05, 0, 0.16);
        this.leftElbow.rotation.x = this.rightElbow.rotation.x = -0.52;
        this.torsoPivot.rotation.x = 0.1;
        this.headPivot.rotation.y = 0.12;
        break;
      case 'kneeling':
        this.position.y -= 0.48;
        this.torsoPivot.rotation.x = 0.22;
        this.leftLeg.rotation.x = -0.75;
        this.rightLeg.rotation.x = 0.28;
        this.leftKnee.rotation.x = 1.35;
        this.rightKnee.rotation.x = 1.1;
        this.leftArm.rotation.x = this.rightArm.rotation.x = -0.72;
        this.leftElbow.rotation.x = this.rightElbow.rotation.x = -0.62;
        this.headPivot.rotation.x = 0.28;
        break;
      case 'bound':
        this.leftArm.rotation.set(-0.62, 0, 0.27);
        this.rightArm.rotation.set(-0.62, 0, -0.27);
        this.leftElbow.rotation.x = this.rightElbow.rotation.x = -0.9;
        this.headPivot.rotation.x = 0.12;
        break;
      case 'cruciform':
        // Shoulder-to-wrist span is 2.47m in rig units and aligns with the
        // central 3.25m beam; hands remain slightly below the beam's top edge.
        {
        const death = THREE.MathUtils.smoothstep(time, 98, 106);
        const breath = (1 - death) * Math.sin(time * 1.08) * 0.007;
        this.leftArm.position.y = 1.51;
        this.rightArm.position.y = 1.55;
        this.leftArm.rotation.set(0.035, 0, -Math.PI / 2 - 0.145);
        this.rightArm.rotation.set(-0.025, 0, Math.PI / 2 + 0.12);
        this.leftElbow.rotation.set(-0.08, 0, -0.12);
        this.rightElbow.rotation.set(0.05, 0, 0.1);
        // The hands are the suspension points: chest and pelvis hang below
        // them, with a small asymmetric collapse rather than a standing T.
        this.torsoPivot.position.y = 0.78 - death * 0.055;
        this.torsoPivot.rotation.set(0.18 + death * 0.07, 0, 0.06 + death * 0.035);
        this.crucifixionBody.position.set(0.035, -0.34 - death * 0.065 + breath, 0.025);
        this.crucifixionBody.rotation.set(0.09, 0, 0.055 + death * 0.035);
        this.crucifixionBody.scale.set(1, 1 + breath, 1);
        this.headPivot.position.y = 1.74 - death * 0.075;
        this.headPivot.rotation.set(0.29 + death * 0.46, 0.035, 0.1 + death * 0.12);
        this.leftLeg.position.set(-0.16, 0.59 - death * 0.045, 0.015);
        this.rightLeg.position.set(0.15, 0.55 - death * 0.06, -0.005);
        this.leftLeg.rotation.set(-0.34, 0.02, -0.1);
        this.rightLeg.rotation.set(-0.12, -0.02, 0.075);
        this.leftKnee.rotation.set(0.72, 0, 0.055);
        this.rightKnee.rotation.set(0.48, 0, -0.045);
        this.woundMarks.visible = true;
        }
        break;
      case 'mourning':
        this.torsoPivot.rotation.x = 0.24;
        this.headPivot.rotation.set(0.43, 0, 0.08);
        this.leftArm.rotation.x = this.rightArm.rotation.x = -0.34;
        this.leftElbow.rotation.x = this.rightElbow.rotation.x = -0.52;
        for (const brow of this.brows) brow.rotation.z = Math.PI / 2 + Math.sign(brow.position.x) * 0.18;
        break;
      case 'reaching':
        this.rightArm.rotation.set(-1.12, 0, 0.08);
        this.rightElbow.rotation.x = -0.38;
        this.torsoPivot.rotation.y = -0.08;
        this.headPivot.rotation.y = -0.12;
        break;
      case 'blessing':
        this.rightArm.rotation.set(-1.12, 0, 0.14);
        this.rightElbow.rotation.x = -0.78;
        this.leftArm.rotation.x = -0.5;
        this.leftElbow.rotation.x = -0.35;
        break;
      default:
        break;
    }
  }

  private static profileFor(spec: CharacterSpec): CostumeProfile {
    let profile: CostumeProfile;
    switch (spec.role) {
      case 'jesus':
        profile = { skin: 0x956246, tunic: 0xa89b80, mantle: 0x74443a, trim: 0x80694a, hair: 0x241915, width: 0.365, longRobe: true };
        break;
      case 'mary':
        profile = { skin: 0x8f5e46, tunic: 0x693844, mantle: 0x35485f, trim: 0x846b4c, hair: 0x231817, width: 0.345, longRobe: true };
        break;
      case 'peter':
        profile = { skin: 0x98654b, tunic: 0x4d5140, mantle: 0x405852, trim: 0x776345, hair: 0x49382e, width: 0.415, longRobe: false };
        break;
      case 'judas':
        profile = { skin: 0x8c5d44, tunic: 0x66633c, mantle: 0x765139, trim: 0x826a45, hair: 0x201714, width: 0.35, longRobe: false };
        break;
      case 'pilate':
        profile = { skin: 0x9b6d58, tunic: 0xb4a486, mantle: 0x7b2f2b, trim: 0x8f7048, hair: 0x3b302a, width: 0.385, longRobe: false };
        break;
      case 'soldier':
        profile = spec.id.endsWith('2')
          ? { skin: 0x76513f, tunic: 0x665848, mantle: 0x4a4e4b, trim: 0x755d3f, hair: 0x201915, width: 0.405, longRobe: false }
          : { skin: 0x9a6950, tunic: 0x733b34, mantle: 0x54473f, trim: 0x87664a, hair: 0x34251f, width: 0.385, longRobe: false };
        break;
      case 'witness':
        profile = spec.id === 'simon'
          ? { skin: 0x81543f, tunic: 0x695746, mantle: 0x414c48, trim: 0x826746, hair: 0x281a16, width: 0.395, longRobe: false }
          : { skin: spec.id.endsWith('2') ? 0x9a684f : 0x85583f, tunic: spec.id.endsWith('2') ? 0x6e594b : 0x5f4b45, mantle: spec.id.endsWith('2') ? 0x4d4351 : 0x3d4d4b, trim: 0x836b4c, hair: 0x251814, width: spec.id.endsWith('2') ? 0.36 : 0.345, longRobe: true };
        break;
      case 'disciple':
        profile = spec.id.endsWith('2')
          ? { skin: 0x9b6a52, tunic: 0x66704e, mantle: 0x684936, trim: 0x856b48, hair: 0x2b1e18, width: 0.345, longRobe: false }
          : { skin: 0x85583f, tunic: 0x78654c, mantle: 0x41534f, trim: 0x806846, hair: 0x30231d, width: 0.39, longRobe: false };
        break;
      default:
        profile = { skin: 0x916047, tunic: 0x766853, mantle: 0x465855, trim: 0x8b704b, hair: 0x281b17, width: 0.365, longRobe: false };
        break;
    }
    if (spec.palette) {
      profile = { ...profile, tunic: spec.palette[0], mantle: spec.palette[1], trim: spec.palette[2] };
    }
    return profile;
  }
}
